import { useEffect, useState } from 'react';
import { API } from '../App';

/**
 * TrackingPixels Component
 * Loads Facebook Pixel, TikTok Pixel, and Google gtag from database config
 * Provides utility functions for firing conversion events
 */
const TrackingPixels = () => {
  const [config, setConfig] = useState(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    // Fetch marketing config from backend
    const fetchConfig = async () => {
      try {
        const response = await fetch(`${API}/marketing/config`);
        const data = await response.json();
        setConfig(data);
      } catch (error) {
        console.warn('Could not load marketing config:', error);
      }
    };

    fetchConfig();
  }, []);

  useEffect(() => {
    if (!config || loaded) return;

    // Load Facebook Pixel
    if (config.facebook_pixel_id) {
      loadFacebookPixel(config.facebook_pixel_id);
    }

    // Load TikTok Pixel
    if (config.tiktok_pixel_id) {
      loadTikTokPixel(config.tiktok_pixel_id);
    }

    // Load Google gtag
    if (config.google_conversion_id) {
      loadGoogleTag(config.google_conversion_id);
    }

    setLoaded(true);
  }, [config, loaded]);

  return null; // This component doesn't render anything
};

// ==================== PIXEL LOADERS ====================

const loadFacebookPixel = (pixelId) => {
  if (window.fbq) return;

  // Facebook Pixel Base Code
  (function(f,b,e,v,n,t,s) {
    if(f.fbq)return;n=f.fbq=function(){n.callMethod?
    n.callMethod.apply(n,arguments):n.queue.push(arguments)};
    if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
    n.queue=[];t=b.createElement(e);t.async=!0;
    t.src=v;s=b.getElementsByTagName(e)[0];
    s.parentNode.insertBefore(t,s)
  })(window, document,'script','https://connect.facebook.net/en_US/fbevents.js');

  window.fbq('init', pixelId);
  window.fbq('track', 'PageView');

  console.log('Facebook Pixel loaded:', pixelId);
};

const loadTikTokPixel = (pixelId) => {
  if (window.ttq) return;

  // TikTok Pixel Base Code
  (function(w, d, t) {
    w.TiktokAnalyticsObject = t;
    var ttq = w[t] = w[t] || [];
    ttq.methods = ["page", "track", "identify", "instances", "debug", "on", "off", "once", "ready", "alias", "group", "enableCookie", "disableCookie"];
    ttq.setAndDefer = function(t, e) {
      t[e] = function() {
        t.push([e].concat(Array.prototype.slice.call(arguments, 0)))
      }
    };
    for (var i = 0; i < ttq.methods.length; i++) {
      ttq.setAndDefer(ttq, ttq.methods[i])
    }
    ttq.instance = function(t) {
      for (var e = ttq._i[t] || [], n = 0; n < ttq.methods.length; n++) {
        ttq.setAndDefer(e, ttq.methods[n])
      }
      return e
    };
    ttq.load = function(e, n) {
      var i = "https://analytics.tiktok.com/i18n/pixel/events.js";
      ttq._i = ttq._i || {};
      ttq._i[e] = [];
      ttq._i[e]._u = i;
      ttq._t = ttq._t || {};
      ttq._t[e] = +new Date;
      ttq._o = ttq._o || {};
      ttq._o[e] = n || {};
      var o = document.createElement("script");
      o.type = "text/javascript";
      o.async = true;
      o.src = i + "?sdkid=" + e + "&lib=" + t;
      var a = document.getElementsByTagName("script")[0];
      a.parentNode.insertBefore(o, a)
    };
    ttq.load(pixelId);
    ttq.page();
  })(window, document, 'ttq');

  console.log('TikTok Pixel loaded:', pixelId);
};

const loadGoogleTag = (conversionId) => {
  if (window.gtag) return;

  // Google gtag.js
  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${conversionId}`;
  document.head.appendChild(script);

  window.dataLayer = window.dataLayer || [];
  window.gtag = function() { 
    window.dataLayer.push(arguments); 
  };
  window.gtag('js', new Date());
  window.gtag('config', conversionId);

  console.log('Google gtag loaded:', conversionId);
};

// ==================== CONVERSION EVENTS ====================

/**
 * Fire CompleteRegistration event on all pixels
 * Call this on registration success page
 */
export const fireCompleteRegistration = async (userData = {}) => {
  try {
    // Get config to ensure we have the IDs
    const response = await fetch(`${API}/marketing/config`);
    const config = await response.json();

    // Facebook CompleteRegistration
    if (config.facebook_pixel_id && window.fbq) {
      window.fbq('track', 'CompleteRegistration', {
        content_name: 'Nzadi Fit Registration',
        status: 'success',
        ...userData
      });
      console.log('Facebook CompleteRegistration fired');
    }

    // TikTok CompleteRegistration
    if (config.tiktok_pixel_id && window.ttq) {
      window.ttq.track('CompleteRegistration', {
        content_name: 'Nzadi Fit Registration',
        ...userData
      });
      console.log('TikTok CompleteRegistration fired');
    }

    // Google Conversion
    if (config.google_conversion_id && window.gtag) {
      window.gtag('event', 'conversion', {
        'send_to': config.google_conversion_id,
        'event_category': 'registration',
        'event_label': 'complete_registration'
      });
      console.log('Google Conversion fired');
    }
  } catch (error) {
    console.warn('Error firing conversion events:', error);
  }
};

/**
 * Fire Purchase event on all pixels
 * Call this on successful payment/subscription
 */
export const firePurchase = async (purchaseData = {}) => {
  try {
    const response = await fetch(`${API}/marketing/config`);
    const config = await response.json();

    const { value = 0, currency = 'AOA', plan = 'subscription' } = purchaseData;

    // Facebook Purchase
    if (config.facebook_pixel_id && window.fbq) {
      window.fbq('track', 'Purchase', {
        content_name: plan,
        content_type: 'subscription',
        value: value,
        currency: currency
      });
      console.log('Facebook Purchase fired');
    }

    // TikTok Purchase
    if (config.tiktok_pixel_id && window.ttq) {
      window.ttq.track('PlaceAnOrder', {
        content_name: plan,
        value: value,
        currency: currency
      });
      console.log('TikTok Purchase fired');
    }

    // Google Purchase Conversion
    if (config.google_conversion_id && window.gtag) {
      window.gtag('event', 'purchase', {
        'send_to': config.google_conversion_id,
        'value': value,
        'currency': currency,
        'transaction_id': Date.now().toString()
      });
      console.log('Google Purchase fired');
    }
  } catch (error) {
    console.warn('Error firing purchase events:', error);
  }
};

export default TrackingPixels;
